import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { useI18n, LANGUAGES } from "../lib/i18n";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash, UploadSimple, VideoCamera, Image as ImageIcon, FloppyDisk } from "@phosphor-icons/react";

const MAX_BYTES = 8 * 1024 * 1024; // 8MB

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function EditProfile() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [skills, setSkills] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  // Profile fields
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [city, setCity] = useState(user?.city || "");
  const [stateName, setStateName] = useState(user?.state || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [yrs, setYrs] = useState(0);
  const [education, setEducation] = useState("");
  const [availability, setAvailability] = useState("gig");
  const [companyName, setCompanyName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [industry, setIndustry] = useState("");
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [teamSize, setTeamSize] = useState(1);

  // Media form
  const [mTitle, setMTitle] = useState("");
  const [mDesc, setMDesc] = useState("");
  const [mFile, setMFile] = useState(null);
  const [mLang, setMLang] = useState("en");
  const [mTags, setMTags] = useState([]);

  useEffect(() => {
    api.get("/skills").then(({ data }) => { setSkills(data.skills); setSectors(data.sectors); });
    if (user) {
      api.get(`/profile/${user.id}`).then(({ data }) => {
        const u = data.user;
        setHeadline(u.headline || "");
        setBio(u.bio || "");
        setSelectedSkills(u.skills || []);
        setYrs(u.years_experience || 0);
        setEducation(u.education || "");
        setAvailability(u.availability || "gig");
        setCompanyName(u.company_name || "");
        setOrgName(u.organization_name || "");
        setIndustry(u.industry || "");
        setSelectedSectors(u.sectors || []);
        setTeamSize(u.team_size || 1);
        setCity(u.city || "");
        setStateName(u.state || "");
      });
      if (user.role === "skilled_individual" || user.role === "msme") {
        api.get(`/media/${user.id}`).then(({ data }) => setMedia(data));
      }
    }
  }, [user]);

  if (!user) return null;
  const role = user.role;

  const toggle = (arr, setArr, v) => {
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { headline, bio, city, state: stateName, phone };
      if (role === "skilled_individual") Object.assign(payload, { skills: selectedSkills, years_experience: Number(yrs), education, availability });
      if (role === "msme") Object.assign(payload, { company_name: companyName, sectors: selectedSectors, skills: selectedSkills, team_size: Number(teamSize) });
      if (role === "employer") Object.assign(payload, { organization_name: orgName, industry });
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success(t("profile_saved"));
    } catch (err) { toast.error(err?.response?.data?.detail || t("error_occurred")); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error("File too large (max 8MB)"); return; }
    setMFile(f);
  };

  const uploadMedia = async (e) => {
    e.preventDefault();
    if (!mFile || !mTitle) { toast.error("Select a file and a title"); return; }
    setUploading(true);
    try {
      const data_url = await readFileAsDataURL(mFile);
      const type = mFile.type.startsWith("video") ? "video" : "image";
      const { data } = await api.post("/media", {
        type, data_url, title: mTitle, description: mDesc, skill_tags: mTags, spoken_language: mLang,
      });
      setMedia([data, ...media]);
      setMTitle(""); setMDesc(""); setMFile(null); setMTags([]);
      if (fileRef.current) fileRef.current.value = "";
      toast.success(t("uploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_occurred"));
    } finally { setUploading(false); }
  };

  const deleteMedia = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      setMedia(media.filter((m) => m.id !== id));
    } catch { toast.error(t("error_occurred")); }
  };

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{t("edit_profile")}</h1>

        <form onSubmit={saveProfile} className="as-card p-7 space-y-6" data-testid="edit-profile-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="as-label">{t("headline")}</label>
              <input className="as-input" value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="edit-headline" />
            </div>
            <div>
              <label className="as-label">{t("phone")}</label>
              <input className="as-input" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="edit-phone" />
            </div>
            <div>
              <label className="as-label">{t("city")}</label>
              <input className="as-input" value={city} onChange={(e) => setCity(e.target.value)} data-testid="edit-city" />
            </div>
            <div>
              <label className="as-label">{t("state")}</label>
              <input className="as-input" value={stateName} onChange={(e) => setStateName(e.target.value)} data-testid="edit-state" />
            </div>
          </div>

          <div>
            <label className="as-label">{t("bio")}</label>
            <textarea className="as-input min-h-[100px]" value={bio} onChange={(e) => setBio(e.target.value)} data-testid="edit-bio" />
          </div>

          {role === "skilled_individual" && (
            <>
              <div>
                <label className="as-label">{t("skills")}</label>
                <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto p-2 border border-[var(--as-border)] rounded-xl bg-[var(--as-bg-alt)]">
                  {skills.map((s) => (
                    <button key={s} type="button" onClick={() => toggle(selectedSkills, setSelectedSkills, s)}
                      className={`as-badge-skill ${selectedSkills.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}
                      data-testid={`edit-skill-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="as-label">{t("years_experience")}</label>
                  <input className="as-input" type="number" min={0} max={60} value={yrs} onChange={(e) => setYrs(e.target.value)} data-testid="edit-years" />
                </div>
                <div>
                  <label className="as-label">{t("availability")}</label>
                  <select className="as-input" value={availability} onChange={(e) => setAvailability(e.target.value)} data-testid="edit-avail">
                    <option value="full_time">Full-time</option>
                    <option value="part_time">Part-time</option>
                    <option value="gig">Gig</option>
                    <option value="remote">Remote</option>
                    <option value="on_site">On-site</option>
                  </select>
                </div>
                <div>
                  <label className="as-label">{t("education")}</label>
                  <input className="as-input" value={education} onChange={(e) => setEducation(e.target.value)} data-testid="edit-edu" />
                </div>
              </div>
            </>
          )}

          {role === "msme" && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="as-label">{t("company_name")}</label>
                  <input className="as-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="edit-company" />
                </div>
                <div>
                  <label className="as-label">{t("team_size")}</label>
                  <input className="as-input" type="number" min={1} value={teamSize} onChange={(e) => setTeamSize(e.target.value)} data-testid="edit-team" />
                </div>
              </div>
              <div>
                <label className="as-label">{t("sectors")}</label>
                <div className="flex flex-wrap gap-2">
                  {sectors.map((s) => (
                    <button key={s} type="button" onClick={() => toggle(selectedSectors, setSelectedSectors, s)}
                      className={`as-badge-skill ${selectedSectors.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="as-label">{t("skills")}</label>
                <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto p-2 border border-[var(--as-border)] rounded-xl bg-[var(--as-bg-alt)]">
                  {skills.map((s) => (
                    <button key={s} type="button" onClick={() => toggle(selectedSkills, setSelectedSkills, s)}
                      className={`as-badge-skill ${selectedSkills.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {role === "employer" && (
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="as-label">{t("organization_name")}</label>
                <input className="as-input" value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="edit-org" />
              </div>
              <div>
                <label className="as-label">{t("industry")}</label>
                <input className="as-input" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="edit-industry" />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button type="submit" disabled={busy} className="btn-primary" data-testid="edit-save">
              <FloppyDisk size={18} weight="duotone" /> {busy ? "..." : t("save")}
            </button>
          </div>
        </form>

        {(role === "skilled_individual" || role === "msme") && (
          <>
            <div className="as-card p-7">
              <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4" style={{fontFamily:"Inter"}}>{t("upload")}</h2>

              <form onSubmit={uploadMedia} className="space-y-4" data-testid="upload-media-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="as-label">{t("title")}</label>
                    <input className="as-input" value={mTitle} onChange={(e) => setMTitle(e.target.value)} required data-testid="media-title" />
                  </div>
                  <div>
                    <label className="as-label">{t("spoken_language")}</label>
                    <select className="as-input" value={mLang} onChange={(e) => setMLang(e.target.value)} data-testid="media-lang">
                      {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="as-label">{t("description")}</label>
                  <textarea className="as-input min-h-[60px]" value={mDesc} onChange={(e) => setMDesc(e.target.value)} data-testid="media-desc" />
                </div>
                <div>
                  <label className="as-label">{t("skill_tags")}</label>
                  <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto p-2 border border-[var(--as-border)] rounded-xl bg-[var(--as-bg-alt)]">
                    {selectedSkills.length === 0 && <span className="text-xs text-[var(--as-text-secondary)] p-1">Add skills above first.</span>}
                    {selectedSkills.map((s) => (
                      <button key={s} type="button" onClick={() => toggle(mTags, setMTags, s)}
                        className={`as-badge-skill ${mTags.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="as-label">File (image or short video, ≤8MB)</label>
                  <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onFile} className="as-input !py-3" data-testid="media-file" />
                </div>
                <button type="submit" disabled={uploading || !mFile} className="btn-primary" data-testid="upload-submit">
                  <UploadSimple size={18} weight="duotone" /> {uploading ? "Uploading..." : t("upload")}
                </button>
                <p className="text-xs text-[var(--as-text-secondary)]">
                  Uploads are auto-screened for inappropriate content.
                </p>
              </form>
            </div>

            <div className="as-card p-7">
              <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4" style={{fontFamily:"Inter"}}>{t("media_gallery")}</h2>
              {media.length === 0 ? (
                <p className="text-[var(--as-text-secondary)]">{t("no_media")}</p>
              ) : (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {media.map((m) => (
                    <div key={m.id} className="as-card !p-0 overflow-hidden">
                      {m.type === "image" ? (
                        <img src={m.data_url} alt={m.title} className="w-full h-40 object-cover" />
                      ) : (
                        <video src={m.data_url} controls className="w-full h-40 bg-black" />
                      )}
                      <div className="p-3">
                        <div className="font-bold text-sm flex items-center gap-1.5">
                          {m.type === "video" ? <VideoCamera size={14} weight="duotone" /> : <ImageIcon size={14} weight="duotone" />}
                          {m.title}
                        </div>
                        <div className="text-xs text-[var(--as-text-secondary)] line-clamp-2">{m.description}</div>
                        <button onClick={() => deleteMedia(m.id)} className="mt-2 text-xs text-red-600 inline-flex items-center gap-1" data-testid={`delete-media-${m.id}`}>
                          <Trash size={14} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
